import { KnownMessageEventType, type Message } from '@openx/components-core/lib/AiChat/types.ts';
import type { TFunction } from 'i18next';

export const AGENT_PROCESSING_TIMEOUT_MS = 1000 * 60 * 10;

function getLastConversationTurn(messages: Message[]): Message[] | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role === 'user') {
      return messages.slice(i);
    }
  }
  return null;
}

function hasMatchingAgentReply(turnMessages: Message[], userMessage: Message): boolean {
  return turnMessages.some(
    message =>
      message.role === 'agent' && (!message.invocation_id || message.invocation_id === userMessage.invocation_id)
  );
}

function getActiveTurnUserMessage(messages: Message[]): Message | null {
  const lastTurn = getLastConversationTurn(messages);
  if (!lastTurn) {
    return null;
  }

  const userMessage = lastTurn.find(message => message.role === 'user');
  if (!userMessage) {
    return null;
  }

  if (hasMatchingAgentReply(lastTurn, userMessage)) {
    return null;
  }

  return userMessage;
}

export function getActiveTurnDeadline(
  messages: Message[],
  now: number = Date.now(),
  timeoutMs: number = AGENT_PROCESSING_TIMEOUT_MS
): number | null {
  const activeTurnUserMessage = getActiveTurnUserMessage(messages);
  if (!activeTurnUserMessage) {
    return null;
  }

  const parsedTimestamp = Date.parse(activeTurnUserMessage.timestamp);
  if (!Number.isFinite(parsedTimestamp)) {
    return null;
  }

  return Math.min(parsedTimestamp, now) + timeoutMs;
}

export function isDealTroubleshootingAgentThinking(
  messages: Message[],
  mutationPending: boolean,
  now: number = Date.now(),
  timeoutMs: number = AGENT_PROCESSING_TIMEOUT_MS
): boolean {
  if (mutationPending) {
    return true;
  }

  const deadline = getActiveTurnDeadline(messages, now, timeoutMs);
  return deadline !== null && deadline > now;
}

function getThinkingEventPrefix(type: KnownMessageEventType, t: TFunction): string {
  return type === KnownMessageEventType.FUNCTION_CALL ? t('Gathering data from') : t('Analyzing data from');
}

function getThinkingEventToolName(name: string, t: TFunction): string {
  switch (name) {
    case 'troubleshoot_deal_tool':
      return t('General Troubleshooting Tool');
    case 'axiomGetDealAnalytics':
      return t('General Troubleshooting Tool');
    case 'axiomGetDealFunnelConversions':
      return t('Funnel Conversions Analysis Tool');
    case 'axiomLossReasonAnalysis':
      return t('Loss Reasons Analysis Tool');
    case 'axiomGetDealNrtMetrics':
      return t('Near Real-Time (NRT) Performance Metrics Tool');
    case 'axiomGetDealAudittrailChanges':
      return t('Deal Configuration History Tool');
    case 'axiomGetDealMetricsByDay':
      return t('Factual Performance Metrics Tool');
    case 'axiomGetDealMetricsByDimension':
      return t('Deal Metrics By Dimensions Tool');
    case 'axiomGetDealTargetingConfiguration':
      return t('Deal Targetings Configuration Tool');
    case 'get_current_time':
    case 'get_deal_info':
    case 'load_mcp_resource':
      return t('internal tools');
    default:
      return name;
  }
}

function isKnownMessageEventType(type: string | undefined): type is KnownMessageEventType {
  return type === KnownMessageEventType.FUNCTION_CALL || type === KnownMessageEventType.FUNCTION_RESPONSE;
}

function isRenderableEvent(event: Message): event is Extract<Message, { role: 'event' }> & {
  content: { type: KnownMessageEventType; name: string };
} {
  if (event.role !== 'event') {
    return false;
  }

  const { content } = event;
  return (
    content !== null &&
    typeof content === 'object' &&
    typeof content.name === 'string' &&
    content.name.length > 0 &&
    isKnownMessageEventType(content.type)
  );
}

export function getEventsLabels(messages: Message[], t: TFunction): string[] {
  const lastConversationTurn = getLastConversationTurn(messages);
  if (!lastConversationTurn) {
    return [];
  }

  const eventLabels: string[] = [];
  for (const event of lastConversationTurn) {
    // Agent steps are untyped JSON, so an unknown or incomplete content shape is skipped.
    if (!isRenderableEvent(event)) {
      continue;
    }

    const prefix = getThinkingEventPrefix(event.content.type, t);
    const toolName = getThinkingEventToolName(event.content.name, t);
    eventLabels.push(`${prefix} ${toolName}`);
  }
  return eventLabels;
}
